/**
 * Timezone helpers for datetime – convert between UTC and a target zone.
 * For Media Devoted | Created By Ciro Autiero
 */

/** Canonical wall clock for TrafficPulse UI (SMSWiser / ops). Same label for every user. */
export const APP_DISPLAY_TIMEZONE = 'Europe/Copenhagen';

/**
 * Get offset in hours for a given (year, month, day) in the timezone.
 * Uses formatToParts for reliable parsing across locales.
 */
function getTimezoneOffsetHours(
  year: number,
  month: number,
  day: number,
  timeZone: string,
): number {
  const noonUtc = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(noonUtc);
  const hourPart = parts.find((p) => p.type === 'hour')?.value;
  if (!hourPart) return 0;
  const zoneHour = parseInt(hourPart, 10);
  return zoneHour - 12;
}

/**
 * Convert "YYYY-MM-DDTHH:mm" in the given zone to UTC ISO string.
 */
export function zoneLocalToUtcIso(localStr: string, timeZone: string): string {
  if (!localStr?.trim()) return '';
  const m = localStr.trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{1,2}):(\d{2})/);
  if (!m) return localStr;
  const [, y, mo, d, h, mi] = m.map(Number);
  const offsetHours = getTimezoneOffsetHours(y, mo, d, timeZone);
  const utcDate = new Date(Date.UTC(y, mo - 1, d, h - offsetHours, mi || 0, 0));
  return utcDate.toISOString();
}

/**
 * Convert UTC ISO string to "YYYY-MM-DDTHH:mm" in the given zone.
 */
export function utcIsoToZoneLocal(iso: string, timeZone: string): string {
  if (!iso?.trim()) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/**
 * Format a Date for display as DD/MM/YYYY HH:mm in the given zone.
 */
export function formatInZone(d: Date, timeZone: string): string {
  if (Number.isNaN(d.getTime())) return '';
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return formatter.format(d).replace(',', '');
}

/**
 * Get (year, month, day) of a Date in the given zone.
 */
export function getDatePartsInZone(
  d: Date,
  timeZone: string,
): { year: number; month: number; day: number } {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(d);
  const get = (type: string) => parseInt(parts.find((p) => p.type === type)?.value ?? '0', 10);
  return { year: get('year'), month: get('month'), day: get('day') };
}

/**
 * Create a Date representing noon on the given (year, month, day) in the zone.
 * Use this for calendar cells so getDatePartsInZone always returns the intended day.
 */
export function createDateAtNoonInZone(
  year: number,
  month: number,
  day: number,
  timeZone: string,
): Date {
  const localStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T12:00`;
  return new Date(zoneLocalToUtcIso(localStr, timeZone));
}

const WEEKDAY_ORDER = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Get weekday 0-6 (Sun-Sat) for a Date in the given zone.
 */
export function getWeekdayInZone(d: Date, timeZone: string): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
  });
  const wd = formatter.format(d);
  return WEEKDAY_ORDER.indexOf(wd);
}

/**
 * Is the given Date before today in the given zone?
 */
export function isBeforeTodayInZone(d: Date, timeZone: string): boolean {
  const parts = getDatePartsInZone(d, timeZone);
  const today = getDatePartsInZone(new Date(), timeZone);
  if (parts.year < today.year) return true;
  if (parts.year > today.year) return false;
  if (parts.month < today.month) return true;
  if (parts.month > today.month) return false;
  return parts.day < today.day;
}
